import {
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore'
import { db } from '@/firebase/init'
import {
  resolvePlaceholders,
  type IResolvePlaceholdersContext,
} from './resolvePlaceholders'

import textListsRaw from './text-lists.json'
import analysisTestsRaw from './analysis-tests.json'
import reportTemplatesRaw from './report-templates.json'
import analysesRaw from './analyses.json'
import addonServicesRaw from './addon-services.json'
import tatsRaw from './tats.json'
import sampleAreasRaw from './sample-areas.json'
import pricingSegmentsRaw from './pricing-segments.json'
import reportDescriptionsRaw from './report-descriptions.json'
import reportDescriptionItemsRaw from './report-description-items.json'
import labEquipmentRaw from './lab-equipment.json'
import labEquipmentMaintenanceRaw from './lab-equipment-maintenance.json'
import closedDaysRaw from './closed-days.json'

const BATCH_SIZE = 500

async function writeDocs(
  collectionPath: [string, ...string[]],
  docs: Record<string, unknown>[]
): Promise<void> {
  if (docs.length === 0) return

  const colRef = collection(db, ...collectionPath)

  for (let i = 0; i < docs.length; i += BATCH_SIZE) {
    const batch = writeBatch(db)
    docs.slice(i, i + BATCH_SIZE).forEach((d) => {
      const docRef = doc(colRef, d.id as string)
      batch.set(docRef, d)
    })
    await batch.commit()
  }
}

/**
 * The three customer price collections. The pricing document's shape is
 * identical across all three customer types — the same fact
 * `customerPricingAdapters.ts` and `runClear.ts` both rely on.
 */
const CUSTOMER_PRICE_COLLECTIONS = [
  'CustomerAccountPrices',
  'InsuranceCustomerAccountPrices',
  'CustomerPrivatePrices',
] as const

/**
 * Points every customer of this lab at the freshly seeded pricing segment.
 *
 * Iterates each price collection directly. The old version derived the
 * `CustomerAccountPrices` doc from `CustomerAccounts` and called `updateDoc` on
 * it, which throws outright when a customer has no pricing document yet — and
 * because this runs last, that abort left the whole seed half-applied, with the
 * catalog written but drop points never given their walk-in TATs. It also only
 * covered business and private customers, so insurance customers kept whatever segment
 * they had, including one just deleted by a preceding "Ryd alt".
 */
async function assignPricingSegment(
  accountID: string,
  pricingSegmentID: string
): Promise<void> {
  for (const collectionName of CUSTOMER_PRICE_COLLECTIONS) {
    const snapshot = await getDocs(
      query(
        collection(db, 'Apps', '-lab-management-', collectionName),
        where('labAccountID', '==', accountID)
      )
    )
    if (snapshot.empty) continue

    const docs = snapshot.docs
    for (let i = 0; i < docs.length; i += BATCH_SIZE) {
      const batch = writeBatch(db)
      docs.slice(i, i + BATCH_SIZE).forEach((priceDoc) => {
        batch.update(priceDoc.ref, {
          pricingSegmentID,
          'timestamps.updatedAt': Date.now(),
        })
      })
      await batch.commit()
    }
  }
}

export async function runSeed(
  accountID: string,
  userID: string
): Promise<void> {
  const context: IResolvePlaceholdersContext = { accountID, userID }

  // Shared ID map — passed to every resolvePlaceholders call so cross-file
  // token references (e.g. {{at-bly}} in both analysis-tests + analyses) resolve
  // to the same Firestore ID within this seed run.
  const idMap: Record<string, string> = {}

  const resolve = (raw: unknown[]) =>
    resolvePlaceholders(JSON.stringify(raw), context, idMap)

  // ── 1. TextLists ─────────────────────────────────────────────────────────────
  await writeDocs(
    ['Apps', '-lab-management-', 'TextLists'],
    resolve(textListsRaw as unknown[])
  )

  // ── 2. AnalysisTests ─────────────────────────────────────────────────────────
  await writeDocs(
    ['Apps', '-lab-management-', 'AnalysisTests'],
    resolve(analysisTestsRaw as unknown[])
  )

  // ── 3. ReportTemplates (before Analyses — analyses reference labReportTemplateID) ──
  await writeDocs(
    ['Apps', '-lab-management-', 'ReportTemplates'],
    resolve(reportTemplatesRaw as unknown[])
  )

  // ── 4. Analyses ───────────────────────────────────────────────────────────────
  await writeDocs(
    ['Apps', '-lab-management-', 'Analyses'],
    resolve(analysesRaw as unknown[])
  )

  // ── 5. TATs (before PricingSegments — pricing segments reference TAT IDs) ────
  await writeDocs(
    ['Apps', '-lab-management-', 'TATs'],
    resolve(tatsRaw as unknown[])
  )

  // ── 6. SampleAreas ────────────────────────────────────────────────────────────
  await writeDocs(
    ['Apps', '-lab-management-', 'SampleAreas'],
    resolve(sampleAreasRaw as unknown[])
  )

  // ── 7. AddonServices (before PricingSegments — pricing segments reference addon IDs) ──
  await writeDocs(
    ['Apps', '-lab-management-', 'AddonServices'],
    resolve(addonServicesRaw as unknown[])
  )

  // ── 8. PricingSegments (after TATs + Analyses + AddonServices) ────────────────
  await writeDocs(
    ['Apps', '-lab-management-', 'PricingSegments'],
    resolve(pricingSegmentsRaw as unknown[])
  )

  // ── 9. ReportDescriptions ─────────────────────────────────────────────────────
  await writeDocs(
    ['Apps', '-lab-management-', 'ReportDescriptions'],
    resolve(reportDescriptionsRaw as unknown[])
  )

  // ── 10. ReportDescriptionItems (after ReportDescriptions — items reference description IDs) ──
  await writeDocs(
    ['Apps', '-lab-management-', 'ReportDescriptionItems'],
    resolve(reportDescriptionItemsRaw as unknown[])
  )

  // ── 11. LabEquipment ──────────────────────────────────────────────────────────
  await writeDocs(
    ['Apps', '-lab-management-', 'LabEquipment'],
    resolve(labEquipmentRaw as unknown[])
  )

  // ── 12. LabEquipmentMaintenance (after LabEquipment — references equipment IDs) ──
  await writeDocs(
    ['Apps', '-lab-management-', 'LabEquipmentMaintenance'],
    resolve(labEquipmentMaintenanceRaw as unknown[])
  )

  // ── 13. ClosedDays (independent — nothing references a closed day) ───────────
  await writeDocs(
    ['Apps', '-lab-management-', 'ClosedDays'],
    resolve(closedDaysRaw as unknown[])
  )

  // ── Put every customer of this lab onto the seeded pricing segment ────────────
  // The idMap now contains 'ps-standard' after step 8.
  const pricingSegmentID = idMap['ps-standard'] ?? null
  if (pricingSegmentID !== null) {
    await assignPricingSegment(accountID, pricingSegmentID)
  }

  // ── Update existing LabDropPoints with the seeded walk-in TAT IDs ─────────────
  // The idMap now contains the walk-in TAT IDs after step 5.
  const walkInTatIDs = [
    idMap['tat-walkin-skimmel'],
    idMap['tat-walkin-asbest'],
  ].filter(Boolean)
  if (walkInTatIDs.length > 0) {
    const dropPointsSnapshot = await getDocs(
      query(
        collection(db, 'Apps', '-lab-management-', 'LabDropPoints'),
        where('labAccountID', '==', accountID)
      )
    )
    for (const dropPointDoc of dropPointsSnapshot.docs) {
      // walkInWindow only exists on 'lab' type drop points — 'private' drop
      // points don't carry walk-in TATs at all.
      if (dropPointDoc.data()['type'] !== 'lab') continue

      await updateDoc(dropPointDoc.ref, {
        walkInTatIDs,
        // Falls well inside the seeded 08:00–17:00 opening hours, so it's
        // valid out of the box instead of showing up as unset in the UI.
        walkInWindow: {
          start: { hours: 8, minutes: 0 },
          end: { hours: 14, minutes: 0 },
        },
      })
    }
  }
}

